import isArray from 'lodash/isArray';
import isBoolean from 'lodash/isBoolean';
import isFunction from 'lodash/isFunction';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { createAction } from 'typesafe-actions';

import LogoPremiumWhiteIcon from '../../static/icons/ic_logo_pandora_premium_white.svg';
import LogoPlusIcon from '../../static/icons/logo_pandora_plus.svg';
import AnotherLogoPremiumWhiteIcon from '../../static/icons/logo_premium_white.svg';
import LogoPremiumFamilyIcon from '../../static/icons/logo-premium-family.svg';
import PlaylistCreateIcon from '../../static/icons/playlist_creation_80.svg';
import { type OverlayConfig, closeOverlay, openOverlay } from '../actions/Overlay';
import Button from '../components/Button/Button';
import ButtonT3 from '../components/lib/ButtonT3/ButtonT3';
import { ButtonT3Size } from '../components/lib/ButtonT3/constants';
import * as Constants from '../constants';
import { adBlockerMessageStrings } from '../constants/BlueBar';
import {
  OVERLAY_ACTIONS,
  OVERLAY_CONFIG,
  OVERLAY_SUBTYPES,
  OVERLAY_TYPES,
} from '../constants/Overlay';
import { Urls } from '../constants/Urls';
import ViewActions from '../constants/ViewActions';
import {
  allowlistPandoraAdBlockerHelpUrl,
  getAutoplayBlockHelperUrl,
  getPremiumUpgradePath,
} from '../lib/RouteUtils';

export const openSignupLoginModal = createAction(ViewActions.OPEN_SIGNUP_LOGIN_MODAL)();
export type OpenSignupLoginModalAction = ReturnType<typeof openSignupLoginModal>;

interface OpenModalCoachmarkParams {
  bodyMessage: ReturnType<typeof FormattedMessage> | string;
  defaultMessage: string;
  id: string;
  values: $TSFixMe;
}

type OpenModalCoachmarkPayload = Omit<OpenModalCoachmarkParams, 'defaultMessage'>;

export const openModalCoachmark = createAction(
  ViewActions.OPEN_MODAL_COACHMARK,
  ({ bodyMessage, id, defaultMessage, values }: Partial<OpenModalCoachmarkParams>) => ({
    bodyMessage: bodyMessage || defaultMessage,
    id,
    values,
  }),
)<OpenModalCoachmarkPayload>();
export type OpenModalCoachmarkAction = ReturnType<typeof openModalCoachmark>;

export const openModalReadOnlyMode = createAction(ViewActions.OPEN_MODAL_READONLYMODE)();
export type OpenModalReadonlymodeAction = ReturnType<typeof openModalReadOnlyMode>;

interface ModalConfig extends $TSFixMe {}

export const openModal = (modalConfig: ModalConfig, id?: $TSFixMe, subType?: $TSFixMe) => {
  return openOverlay({
    [OVERLAY_CONFIG.ID]: id,
    [OVERLAY_CONFIG.TYPE]: OVERLAY_TYPES.modal,
    [OVERLAY_CONFIG.SUBTYPE]: subType || modalConfig.modalType,
    [OVERLAY_CONFIG.PROPS]: modalConfig,
  });
};

type CloseOverlayParams = $TSFixMe;

interface CloseOverlayPayload {
  overlayConfig: OverlayConfig;
}

export const closeModal = createAction(
  OVERLAY_ACTIONS.CLOSE_OVERLAY,
  (_id?: CloseOverlayParams) => {
    const id = typeof _id === 'string' ? _id : null;
    return {
      overlayConfig: {
        [OVERLAY_CONFIG.ID]: id,
        [OVERLAY_CONFIG.TYPE]: OVERLAY_TYPES.modal,
      },
    };
  },
)<CloseOverlayPayload>();
export type CloseOverlayAction = ReturnType<typeof closeModal>;

export const openModalCoachmarkWithButton = ({
  bodyMessage,
  buttonMessage,
  buttonCallback,
  buttonHref,
  ...otherProps
}: $TSFixMe) => {
  // TODO: ButtonT3Props
  const buttonProps: $TSFixMe = {
    size: ButtonT3Size.SMALL,
  };

  if (buttonCallback) {
    buttonProps.onClick = buttonCallback;
  } else if (buttonHref) {
    buttonProps.href = buttonHref;
  }

  const modalConfig = Object.assign({}, otherProps, {
    modalType: 'ModalCoachMark',
    title: '',
    body: bodyMessage,
    buttons: [
      <ButtonT3 key={0} {...buttonProps}>
        {buttonMessage}
      </ButtonT3>,
    ],
  });
  let styleNames = ['withButton'];
  if (isArray(modalConfig.styleNames)) {
    styleNames = styleNames.concat(modalConfig.styleNames);
  }
  modalConfig.styleNames = styleNames;
  return openModal(modalConfig);
};

export const openUnsavedChangesModal = ({ okCallback, closeCallback }: $TSFixMe) =>
  openModal({
    modalType: 'ModalStandard',
    title: <FormattedMessage {...Constants.UserSettings.messages.confirmNavigationTitle} />,
    body: <FormattedMessage {...Constants.UserSettings.messages.confirmNavigationBody} />,
    onClose: closeCallback,
    hideXRow: true,
    buttons: [
      <Button
        key={0}
        classes={['Button--white']}
        onClick={closeCallback}
        qaTag={Constants.QATags.CB}
      >
        <FormattedMessage {...Constants.UserSettings.messages.buttonCancel} />
      </Button>,
      <Button key={1} onClick={okCallback} qaTag={Constants.QATags.OB}>
        <FormattedMessage {...Constants.UserSettings.messages.buttonLeavePage} />
      </Button>,
    ],
  });

/**
 *
 * @param {object} narrative
 * @param {Function | boolean | object | string} closeCallbackOrText
 * @param {object} options
 * @returns {object}
 */
export const openNarrativeModal = (
  narrative: $TSFixMe,
  closeCallbackOrText: $TSFixMe,
  options: $TSFixMe = {},
) => {
  let closeCallback;
  let closeButtonText;
  let buttons;

  if (isFunction(closeCallbackOrText)) {
    closeCallback = closeCallbackOrText;
    buttons = [
      <Button key={0} onClick={closeCallback}>
        <FormattedMessage {...Constants.Modal.messages.close} />
      </Button>,
    ];
  } else if (isBoolean(closeCallbackOrText)) {
    closeButtonText = <FormattedMessage {...Constants.Modal.messages.close} />;
  } else if (typeof closeCallbackOrText !== 'undefined') {
    closeButtonText = closeCallbackOrText;
  }

  return openModal({
    modalType: 'ModalStandard',
    body: narrative,
    hideXRow: true,
    isNoMarginBody: options.isNoMarginBody,
    isLeftAlignedBody: options.isLeftAlignedBody,
    closeButtonText,
    buttons,
  });
};

export const openPremiumUpsellModal = (pandoraType: $TSFixMe, isTrialEligible: boolean) => {
  const {
    premiumUpsellTitleForAlbum,
    premiumUpsellBodyForAlbum,
    premiumUpsellTitleForPlaylist,
    premiumUpsellBodyForPlaylist,
    premiumUpsellTitleForTrack,
    premiumUpsellBodyForTrack,
    startFreeTrial,
    upgrade,
    notNow,
  } = Constants.Modal.messages;

  let title;
  let body;

  if (pandoraType === Constants.SourceTypes.PandoraTypes.ALBUM) {
    title = premiumUpsellTitleForAlbum;
    body = premiumUpsellBodyForAlbum;
  } else if (pandoraType === Constants.SourceTypes.PandoraTypes.PLAYLIST) {
    title = premiumUpsellTitleForPlaylist;
    body = premiumUpsellBodyForPlaylist;
  } else {
    title = premiumUpsellTitleForTrack;
    body = premiumUpsellBodyForTrack;
  }

  return openOverlay({
    [OVERLAY_CONFIG.TYPE]: OVERLAY_TYPES.modal,
    [OVERLAY_CONFIG.SUBTYPE]: OVERLAY_SUBTYPES.MODAL_COACHMARK_DIALOG,
    [OVERLAY_CONFIG.PROPS]: {
      iconGlyph: LogoPremiumWhiteIcon,
      iconStyleNames: Constants.Modal.ModalStyles.PANDORA_PREMIUM,
      title: <FormattedMessage {...title} />,
      body: <FormattedMessage {...body} />,
      ctaButtonText: <FormattedMessage {...(isTrialEligible ? startFreeTrial : upgrade)} />,
      ctaButtonUrl: getPremiumUpgradePath(),
      closeButtonText: <FormattedMessage {...notNow} />,
    },
  });
};

export const openIsSharedStationModal = (transformStation: $TSFixMe, onClose: $TSFixMeFunction) =>
  openModal({
    modalType: 'ModalStandard',
    title: <FormattedMessage {...Constants.SharedStation.messages.header} />,
    body: <FormattedMessage {...Constants.SharedStation.messages.bodyMessage} />,
    onClose,
    hideXRow: true,
    buttons: [
      <Button key={0} classes={['Button--white ']} onClick={onClose} qaTag={Constants.QATags.CB}>
        <FormattedMessage {...Constants.SharedStation.messages.button2} />
      </Button>,
      <Button key={1} onClick={transformStation} qaTag={Constants.QATags.COB}>
        <FormattedMessage {...Constants.SharedStation.messages.button1} />
      </Button>,
    ],
  });

export const openWelcomeParentToPremiumFamilyModal = ({
  ctaButtonUrl,
  closeButtonUrl,
  ctaButtonFunc,
  closeButtonFunc,
  ctaButtonShouldNotClose,
  closeButtonShouldNotClose,
  partner,
  partnerUpgradePlanName,
}: $TSFixMe) =>
  openOverlay({
    [OVERLAY_CONFIG.TYPE]: OVERLAY_TYPES.modal,
    [OVERLAY_CONFIG.SUBTYPE]: OVERLAY_SUBTYPES.MODAL_COACHMARK_DIALOG,
    [OVERLAY_CONFIG.PROPS]: {
      iconGlyph: LogoPremiumFamilyIcon,
      iconStyleNames: Constants.Modal.ModalStyles.PANDORA_PREMIUM_FAMILY,
      isBlueOverlay: true,
      timeout: null,
      className: Constants.Modal.ModalStyles.PANDORA_PREMIUM_FAMILY,
      title: partner ? (
        <FormattedMessage
          {...Constants.Modal.messages.welcomeToPandoraTitle}
          values={{ planName: partnerUpgradePlanName }}
        />
      ) : (
        <FormattedMessage {...Constants.Modal.messages.welcomeToPandoraPremiumFamilyTitle} />
      ),
      body: partner ? (
        <FormattedMessage
          {...Constants.Modal.messages.welcomeToPandoraPremiumFamilyPartnerUpgradeBody}
          values={{
            welcomeToPandoraPartnerUpgradeBody: (
              <FormattedMessage
                {...Constants.Modal.messages.welcomeToPandoraPartnerUpgradeBody}
                values={{ planName: partnerUpgradePlanName, partner }}
              />
            ),
            welcomeToPandoraPremiumFamilyBody: (
              <FormattedMessage {...Constants.Modal.messages.welcomeToPandoraPremiumFamilyBody} />
            ),
          }}
        />
      ) : (
        <FormattedMessage {...Constants.Modal.messages.welcomeToPandoraPremiumFamilyBody} />
      ),
      isTranslucentBodyText: true,
      ctaButtonText: (
        <FormattedMessage {...Constants.Modal.messages.welcomeToPandoraPremiumFamilyInviteButton} />
      ),
      ctaButtonUrl,
      closeButtonText: (
        <FormattedMessage {...Constants.Modal.messages.welcomeToPandoraPremiumFamilySkipButton} />
      ),
      closeButtonUrl,
      ctaButtonFunc,
      closeButtonFunc,
      ctaButtonShouldNotClose,
      closeButtonShouldNotClose,
    },
  });

export const openUnableToJoinFamilyModal = (message: $TSFixMe) =>
  openModal({
    modalType: Constants.Modal.ModalTypes.STANDARD,
    title: <FormattedMessage {...Constants.Modal.messages.unableToJoinFamily} />,
    body: message,
    isSmall: true,
    closeButtonText: <FormattedMessage {...Constants.Modal.messages.dismiss} />,
  });

export const openPartnerUpgradeConfirmUserModal = (modalConfig: ModalConfig) => {
  const { listenerEmail, partnerUpgradePlanName, partner } = modalConfig;
  let logo;
  if (partnerUpgradePlanName === 'Pandora Plus') {
    logo = LogoPlusIcon;
  } else if (partnerUpgradePlanName === 'Pandora Premium Family') {
    logo = LogoPremiumFamilyIcon;
  } else {
    logo = AnotherLogoPremiumWhiteIcon;
  }
  return openOverlay({
    [OVERLAY_CONFIG.TYPE]: OVERLAY_TYPES.modal,
    [OVERLAY_CONFIG.SUBTYPE]: OVERLAY_SUBTYPES.MODAL_COACHMARK_DIALOG,
    [OVERLAY_CONFIG.PROPS]: {
      iconGlyph: logo,
      iconStyleNames: Constants.Modal.ModalStyles.PANDORA_PARTNER_UPGRADE,
      isBlueOverlay: true,
      timeout: null,
      className: Constants.Modal.ModalStyles.PANDORA_PARTNER_UPGRADE,
      title: (
        <FormattedMessage
          {...Constants.Modal.messages.confirmUserPartnerUpgradeHeader}
          values={{
            planName: partnerUpgradePlanName,
            partner: <span className='Modal__container__title__partnerName'>{partner}</span>,
          }}
        />
      ),
      body: (
        <FormattedMessage
          {...Constants.Modal.messages.loggedInAsUser}
          values={{
            listenerEmail: <b>{listenerEmail}</b>,
          }}
        />
      ),
      isTranslucentBodyText: true,
      ctaButtonText: <FormattedMessage {...Constants.Modal.messages.continue} />,
      ctaButtonFunc: modalConfig.ctaCallback,
      closeButtonText: <FormattedMessage {...Constants.Modal.messages.logInWithDifferentAccount} />,
      closeButtonFunc: modalConfig.logInDifferentUserPartnerUpgrade,
      footerContent: (
        <FormattedMessage
          {...Constants.Modal.messages.legalese}
          values={{
            privacyAndTerms: (
              <ul className='Modal--coachmarkDialogFooter__list'>
                <li className='Modal--coachmarkDialogFooter__list__item'>
                  <a target='_external' href={Urls.TERMS}>
                    <FormattedMessage {...Constants.Modal.messages.terms} />
                  </a>
                </li>
                <li className='Modal--coachmarkDialogFooter__list__item'>|</li>
                <li className='Modal--coachmarkDialogFooter__list__item'>
                  <a target='_external' href={Urls.PRIVACY}>
                    <FormattedMessage {...Constants.Modal.messages.privacy} />
                  </a>
                </li>
              </ul>
            ),
            termsAndConditions: (
              <p>
                <FormattedMessage
                  {...Constants.Modal.messages.termsAndConditions}
                  values={{
                    partner,
                    subscriptionTerms: (
                      <a
                        className='Modal--coachmarkDialogFooter__a'
                        target='_external'
                        href={Urls.SUBSCRIPTION_TERMS}
                      >
                        <FormattedMessage {...Constants.Modal.messages.subscriptionTerms} />
                      </a>
                    ),
                  }}
                />
              </p>
            ),
          }}
        />
      ),
    },
  });
};

export const openWelcomePartnerUpgradeModal = (modalConfig: ModalConfig) => {
  const { startListeningClick, ctaButtonShouldNotClose, partnerUpgradePlanName, partner } =
    modalConfig;
  return openOverlay({
    [OVERLAY_CONFIG.TYPE]: OVERLAY_TYPES.modal,
    [OVERLAY_CONFIG.SUBTYPE]: OVERLAY_SUBTYPES.MODAL_COACHMARK_DIALOG,
    [OVERLAY_CONFIG.PROPS]: {
      iconGlyph:
        partnerUpgradePlanName === 'Pandora Plus' ? LogoPlusIcon : AnotherLogoPremiumWhiteIcon,
      iconStyleNames: Constants.Modal.ModalStyles.PANDORA_PARTNER_UPGRADE,
      isBlueOverlay: true,
      timeout: null,
      className: Constants.Modal.ModalStyles.PANDORA_PARTNER_UPGRADE_WELCOME,
      title: (
        <FormattedMessage
          {...Constants.Modal.messages.welcomeToPandoraTitle}
          values={{ planName: partnerUpgradePlanName }}
        />
      ),
      body: (
        <FormattedMessage
          {...Constants.Modal.messages.welcomeToPandoraPartnerUpgradeBody}
          values={{
            planName: partnerUpgradePlanName,
            partner: <span className='Modal__container__title__partnerName'>{partner}</span>,
          }}
        />
      ),
      isTranslucentBodyText: true,
      ctaButtonText: <FormattedMessage {...Constants.Modal.messages.startListening} />,
      ctaButtonFunc: startListeningClick,
      ctaButtonShouldNotClose,
    },
  });
};

export const openPartnerUpgradeIneligibleModal = (modalConfig: ModalConfig) => {
  const { listenerEmail, partnerUpgradePlanName, partner } = modalConfig;
  return openModal({
    modalType: 'ModalStandard',
    isSubscriptionModal: true,
    hideXRow: true,
    isWideModal: true,
    isNoMarginBody: true,
    shouldStackAndReverseButtons: true,
    isDismissable: false,
    body: (
      <FormattedMessage
        {...modalConfig.ineligiblePartnerUpgradeMessage}
        values={{
          listenerEmail: <b>{listenerEmail}</b>,
          planName: partnerUpgradePlanName,
          partner,
        }}
      />
    ),
    buttons: [
      <Button
        key={0}
        classes={['Button--white']}
        onClick={modalConfig.registerNewUserPartnerUpgrade}
      >
        <FormattedMessage {...modalConfig.createNewAccount} />
      </Button>,
      <Button key={1} onClick={modalConfig.logInDifferentUserPartnerUpgrade}>
        <FormattedMessage {...modalConfig.loginDifferentAccount} />
      </Button>,
    ],
  });
};

export const openPartnerUpgradeAgeIneligibleModal = (modalConfig: ModalConfig) => {
  return openModal({
    modalType: 'ModalStandard',
    isSubscriptionModal: true,
    hideXRow: true,
    isWideModal: true,
    isNoMarginBody: true,
    shouldStackAndReverseButtons: true,
    body: (
      <div>
        <p>
          <b>
            <FormattedMessage {...modalConfig.ageIneligiblePartnerUpgradeMessageBody} />
          </b>
        </p>
        <p>
          <FormattedMessage {...modalConfig.ageIneligiblePartnerUpgradeMessageSubBody} />
        </p>
      </div>
    ),
    buttons: [
      <Button
        key={0}
        classes={['Button--white']}
        onClick={modalConfig.registerNewUserPartnerUpgrade}
      >
        <FormattedMessage {...modalConfig.createNewAccount} />
      </Button>,
      <Button key={1} onClick={modalConfig.logInDifferentUserPartnerUpgrade}>
        <FormattedMessage {...modalConfig.loginDifferentAccount} />
      </Button>,
    ],
  });
};

export const openPartnerUpgradeErrorOverlay = (modalConfig: ModalConfig) => {
  const { partner } = modalConfig;
  return openOverlay({
    [OVERLAY_CONFIG.TYPE]: OVERLAY_TYPES.modal,
    [OVERLAY_CONFIG.SUBTYPE]: OVERLAY_SUBTYPES.MODAL_COACHMARK_DIALOG,
    [OVERLAY_CONFIG.PROPS]: {
      isBlueOverlay: true,
      timeout: null,
      title: <FormattedMessage {...Constants.Modal.messages.somethingGoneWrongPartnerUpgrade} />,
      body: (
        <FormattedMessage
          {...Constants.Modal.messages.returnToPartnerSite}
          values={{
            partner: <span className='Modal__container__title__partnerName'>{partner}</span>,
          }}
        />
      ),
    },
  });
};

export const openSomethingGoneWrongModal = (message: $TSFixMe) =>
  openModal({
    modalType: Constants.Modal.ModalTypes.STANDARD,
    title: <FormattedMessage {...Constants.Modal.ErrorMessages.oops} />,
    body: message || <FormattedMessage {...Constants.Modal.ErrorMessages.somethingGoneWrong} />,
    isSmall: true,
    closeButtonText: <FormattedMessage {...Constants.Modal.messages.okay} />,
  });

export const openServiceErrorToast = () =>
  openModal({
    modalType: 'ModalCoachMark',
    title: '',
    body: (
      <FormattedMessage
        {...Constants.ServiceErrors.ServiceErrorMessages.genericServiceErrorMessage}
      />
    ),
    buttons: [],
  });

export const openPodcastEpisodeUnavailable = () =>
  openModal({
    modalType: 'ModalCoachMark',
    title: '',
    body: (
      <FormattedMessage
        {...Constants.ServiceErrors.ServiceErrorMessages.podcastEpisodeUnavailable}
      />
    ),
    buttons: [],
  });

export const openAdBlockerModal = (modalConfig: ModalConfig) =>
  openModal({
    modalType: 'ModalStandard',
    isSubscriptionModal: false,
    hideXRow: true,
    isWideModal: true,
    isNoMarginBody: true,
    body: (
      <div className='Modal__container__body--adBlocker'>
        <h1>
          <FormattedMessage {...Constants.Modal.messages.adBlockerHeadline} />
        </h1>
        <h3 className='Modal__container__body--noMargin Modal__container__body--leftAligned'>
          <FormattedMessage {...Constants.Modal.messages.adBlockerBodyForAntiAdBlockerExperiment} />
        </h3>
      </div>
    ),
    buttons: [
      <a
        key='adBlockerShowMeHowButton'
        className='Button Button--white'
        href={allowlistPandoraAdBlockerHelpUrl}
        target='_blank'
        onClick={modalConfig.showMeHow}
        rel='noreferrer'
      >
        <FormattedMessage {...Constants.Modal.messages.adBlockerCTA} />
      </a>,
      <Button key='adBlockerCloseButton' onClick={modalConfig.closeModal}>
        <FormattedMessage {...Constants.Modal.messages.okay} />
      </Button>,
    ],
  });

export const openAdBlockerInfoMessageBar = (modalConfig: ModalConfig) => {
  return openOverlay({
    [Constants.Overlay.OVERLAY_CONFIG.TYPE]: Constants.Overlay.OVERLAY_TYPES.blueBar,
    [Constants.Overlay.OVERLAY_CONFIG.SUBTYPE]: Constants.Overlay.AdBlockerMessageBlueBarSubtype,
    [Constants.Overlay.OVERLAY_CONFIG.PROPS]: {
      message: adBlockerMessageStrings.adBlockerInfoMessage,
      buttonLink: allowlistPandoraAdBlockerHelpUrl,
      handleClick: () => {
        modalConfig.handleClick();
      },
      buttonText: adBlockerMessageStrings.adsShowMeHowButton,
      onClose: () => {
        modalConfig.closeModal();
      },
    },
  });
};

export const closeAdBlockerInfoMessageBar = () =>
  closeOverlay({
    [Constants.Overlay.OVERLAY_CONFIG.TYPE]: Constants.Overlay.OVERLAY_TYPES.blueBar,
    [Constants.Overlay.OVERLAY_CONFIG.SUBTYPE]: Constants.Overlay.AdBlockerMessageBlueBarSubtype,
  });

export const openBrowserAutoplayBlockModal = (modalConfig: ModalConfig) =>
  openModal({
    modalType: 'ModalStandard',
    isSubscriptionModal: false,
    hideXRow: true,
    isWideModal: true,
    isNoMarginBody: true,
    body: (
      <div>
        <h1>
          <FormattedMessage {...Constants.Modal.messages.browserAutoplayBlockHeadline} />
        </h1>
        <h3 className='Modal__container__body--noMargin Modal__container__body--leftAligned'>
          <FormattedMessage {...Constants.Modal.messages.browserAutoplayBlockBody} />
        </h3>
      </div>
    ),
    buttons: [
      <a
        key={0}
        className='Button Button--white'
        href={getAutoplayBlockHelperUrl()}
        target='_blank'
        onClick={modalConfig.showMeHow}
        rel='noreferrer'
      >
        <FormattedMessage {...Constants.Modal.messages.browserAutoplayBlockCTA} />
      </a>,
      <ButtonT3 key={1} isInline={true} onClick={modalConfig.notNow}>
        <FormattedMessage {...Constants.Modal.messages.notNow} />
      </ButtonT3>,
    ],
  });

export const openLinkedPlaylistUnlockedModal = (modalConfig: ModalConfig) => {
  const message = {
    id: 't.premiumLinkedPlaylist',
    defaultMessage: modalConfig.name.concat(
      ', a playlist of your favorites from this station, has just been created. Every song you thumb up on this station will be automatically added to this playlist.',
    ),
  };
  return openModal({
    modalType: 'ModalStandard',
    isSubscriptionModal: false,
    hideXRow: true,
    isWideModal: true,
    isNoMarginBody: true,
    body: (
      <div className='Modal__container__body--linkedPlaylist'>
        <div className='Modal__container__icon--linkedPlaylist'>
          <PlaylistCreateIcon className='icon-device' />
        </div>
        <div>
          <h2>
            <FormattedMessage {...Constants.Modal.messages.premiumLinkedPlaylistTitle} />
          </h2>
          <h3 className='Modal__container__body--noMargin Modal__container__body--leftAligned'>
            <FormattedMessage {...message} />
          </h3>
        </div>
      </div>
    ),
    buttons: [
      // eslint-disable-next-line jsx-a11y/anchor-is-valid
      <a key={0} href='#' className='Button Button--white' onClick={modalConfig.closeModal}>
        <FormattedMessage {...Constants.Modal.messages.close} />
      </a>,
      <ButtonT3 key={1} isInline={true} onClick={modalConfig.viewPlaylist}>
        <FormattedMessage {...Constants.Modal.messages.viewPlaylist} />
      </ButtonT3>,
    ],
  });
};
